import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import * as Util from '../../common/utilities';
import { withSession } from '../../i18n/containers/AppInit.js';

import '../../ecs/containers/Home.scss';

import {
  Button,
  ButtonGroup,
  Dropdown,
  PageHeaderControl,
  Spinner,
  Toast,
  ToastContainer
} from '@salesforce/design-system-react';

import { default as Actions } from '../actions';
import { default as ECSActions } from '../../ecs/actions';
import * as CommonActions from '../../common/actions';

import { BrandBand } from '../../common/components';
import {
  EctHeader,
  ArchiveExperimentModal,
  DeleteExperimentModal,
  EndExperimentModal,
  ExperimentEditWizard,
  ExperimentPerformance
} from '../components/';
import { McMenu } from '../../ecs/components/';

export class ExperimentAnalytics extends Component {
  componentDidMount() {
    const { experimentId } = this.props.match.params;
    this.props.actions.getExperiment(experimentId, 'analytics');
  }

  componentDidUpdate(prevProps) {
    if (Util.propChanged(prevProps, this.props, 'experiments.data.lastSave')) {
      if (this.props.experiments.data.lastSave) {
        const { lastSave } = this.props.experiments.data;
        if (lastSave.error) {
          this.props.actions.openToast('apiError', {
            ...lastSave.error
          });
        } else if (
          lastSave.mode === 'deactivate' ||
          lastSave.mode === 'activate'
        ) {
          this.props.actions.openToast('experiment', {
            name: lastSave.selectable.name,
            mode: lastSave.mode
          });
          this.props.actions.getExperiment(
            lastSave.selectable.experiment_uuid,
            'analytics'
          );
        } else if (lastSave.mode === 'winner') {
          return;
        } else {
          this.props.actions.openToast('experiment', {
            name: lastSave.experiments.name,
            mode: lastSave.mode
          });
          this.props.actions.getExperiment(
            lastSave.experiments.uuid,
            'analytics'
          );
        }
      }
    }
  }

  getI18nID(suffix) {
    return `components.experiment.${suffix}`;
  }

  publishExperiment() {
    this.props.actions.publishExperiment(
      this.props.experiments?.data?.experiment?.analytics,
      'analytics'
    );
  }

  endExperiment() {
    this.props.actions.openModal(
      'endExperiment',
      this.props.experiments?.data?.experiment?.analytics
    );
  }

  archiveExperiment() {
    this.props.actions.openModal(
      'archiveExperiment',
      this.props.experiments?.data?.experiment?.analytics
    );
  }

  editExperiment() {
    const experiment = this.props.experiments?.data?.experiment?.analytics;
    this.props.actions.openModal('experimentWizard', {
      mode: 'edit',
      experiment
    });
  }

  deleteExperiment() {
    this.props.actions.openModal(
      'deleteExperiment',
      this.props.experiments?.data?.experiment?.analytics
    );
  }

  experimentToast() {
    const { formatMessage } = this.props.intl;
    if (
      this.props.toast.experiment &&
      this.props.toast.experiment.mode !== 'winner'
    ) {
      const heading = formatMessage(
        {
          id: `components.experiment.toast.${this.props.toast.experiment.mode}`
        },
        { experiment: this.props.toast.experiment.name }
      );
      return (
        <Toast
          labels={{ heading }}
          variant="success"
          duration={10000}
          onRequestClose={() => this.props.actions.closeToast('experiment')}
        />
      );
    }

    return null;
  }

  toast() {
    return <React.Fragment>{this.experimentToast()}</React.Fragment>;
  }

  headerDetails() {
    const experiment = this.props.experiments?.data?.experiment?.analytics;

    const { formatMessage } = this.props.intl;

    const testMode = experiment?.engine_name
      ? formatMessage({
          id: this.getI18nID(`engineName.${experiment.engine_name}`)
        })
      : '';
    const status = experiment?.status
      ? formatMessage({ id: this.getI18nID(`testStatus.${experiment.status}`) })
      : '';

    return [
      {
        label: formatMessage({ id: this.getI18nID('status') }),
        content: status
      },
      {
        label: formatMessage({ id: this.getI18nID('testMode') }),
        content: testMode
      },
      {
        label: formatMessage({ id: this.getI18nID('createdBy') }),
        content: experiment?.created_by
      }
    ];
  }

  onMenuSelect({ value }) {
    switch (value) {
      case 'delete':
        this.deleteExperiment();
        break;
      default:
        // For linter
        break;
    }
  }

  headerButtons() {
    const { formatMessage } = this.props.intl;
    const isReadonly = this.props.flags?.flags?.isReadonly;
    const status =
      this.props.experiments?.data?.experiment?.analytics?.status || '';
    const archived =
      this.props.experiments?.data?.experiment?.analytics?.archived || false;
    const imageMinimum =
      this.props.experiments?.data?.experiment?.analytics?.content_test_assets
        ?.length < 2;

    const buildOption = value => ({
      label: formatMessage({
        id: this.getI18nID(`${value}.button`)
      }),
      value
    });
    const statusOptions = status === 'DRAFT' ? ['delete'] : [];
    const options = statusOptions.map(buildOption);

    let firstButton;
    let buttonGroup;
    switch (status) {
      case 'DRAFT':
        firstButton = (
          <Button
            label={formatMessage({ id: this.getI18nID('publish.button') })}
            onClick={this.publishExperiment.bind(this)}
            disabled={isReadonly || imageMinimum}
          />
        );
        buttonGroup = (
          <ButtonGroup variant="list" id="asset-button-list">
            <Button
              label={formatMessage({ id: this.getI18nID('edit.button') })}
              onClick={this.editExperiment.bind(this)}
              disabled={isReadonly}
            />
            <Dropdown
              assistiveText={{ icon: 'More Options' }}
              buttonVariant="icon"
              iconCategory="utility"
              iconName="down"
              iconVariant="border-filled"
              align="right"
              width="x-small"
              onSelect={this.onMenuSelect.bind(this)}
              options={options}
              disabled={isReadonly}
            />
          </ButtonGroup>
        );
        break;
      case 'PUBLISHED':
        firstButton = (
          <Button
            label={formatMessage({ id: this.getI18nID('end.button') })}
            onClick={this.endExperiment.bind(this)}
            disabled={isReadonly}
          />
        );
        buttonGroup = (
          <Button
            label={formatMessage({ id: this.getI18nID('edit.button') })}
            onClick={this.editExperiment.bind(this)}
            disabled={isReadonly}
          />
        );
        break;
      case 'FINISHED':
        buttonGroup = null;
        firstButton = (
          <Button
            label={formatMessage({
              id: archived
                ? this.getI18nID('unarchive.button')
                : this.getI18nID('archive.button')
            })}
            onClick={this.archiveExperiment.bind(this)}
            disabled={isReadonly}
          />
        );
        break;
      default:
        // For linter
        break;
    }

    return () => (
      <PageHeaderControl>
        {firstButton}
        {buttonGroup}
      </PageHeaderControl>
    );
  }

  renderSpinner() {
    return (
      <div className="spinner">
        <Spinner
          size="large"
          variant="brand"
          assistiveText={{ label: 'Large spinner' }}
        />
      </div>
    );
  }

  renderPerformance(session) {
    return (
      <ExperimentPerformance
        actions={this.props.actions}
        jobsLoading={this.props.experiments?.jobsLoading}
        experiment={this.props.experiments?.data?.experiment?.analytics}
        analytics={this.props.experiments?.data?.analytics}
        modals={this.props.modals}
        flags={this.props.flags?.flags}
        session={session}
      />
    );
  }

  render() {
    const { actions, assets, contentBuilder, experiments, modals } =
      this.props || [];
    const flags = this.props.flags?.flags;
    const loading =
      this.props.experiments?.loadContext?.analytics === false ? false : true;
    const context = 'analytics';
    return withSession(session => {
      return (
        <McMenu flags={flags} history={this.props.history} route="/experiments">
          <BrandBand>
            <EctHeader
              route={this.props.match}
              onRenderActions={this.headerButtons()}
              experiment={experiments?.data?.experiment?.analytics}
              details={this.headerDetails()}
            />
            {loading && loading === true
              ? this.renderSpinner()
              : this.renderPerformance(session)}
            <ArchiveExperimentModal
              actions={actions}
              context={context}
              modals={modals}
              flags={flags}
              session={session}
            />
            <DeleteExperimentModal
              actions={actions}
              context={context}
              history={this.props.history}
              modals={modals}
              flags={flags}
              session={session}
            />
            <EndExperimentModal
              actions={actions}
              context={context}
              modals={modals}
              flags={flags}
              session={session}
            />
            <ExperimentEditWizard
              actions={actions}
              assets={assets}
              contentBuilder={contentBuilder}
              context={context}
              modals={modals}
              experiments={experiments}
              finishDisabled={true}
            />
            <ToastContainer>{this.toast()}</ToastContainer>
          </BrandBand>
        </McMenu>
      );
    });
  }
}

ExperimentAnalytics.contextTypes = {
  router: PropTypes.object
};

ExperimentAnalytics.propTypes = {
  actions: PropTypes.object,
  assets: PropTypes.object,
  contentBuilder: PropTypes.object,
  experiments: PropTypes.object.isRequired,
  flags: PropTypes.object,
  location: PropTypes.object,
  modals: PropTypes.object,
  toast: PropTypes.object
};

function mapStateToProps(state) {
  return {
    assets: state.ecs.assets,
    contentBuilder: state.ecs.contentBuilder,
    experiments: state.experiments,
    flags: state.ecs.flags,
    modals: state.modals,
    toast: state.toast
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      { ...Actions, ...ECSActions, ...CommonActions },
      dispatch
    )
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(injectIntl(ExperimentAnalytics));
